import { isDeletedPath } from '@/recycle/store';
import { lookup, lookupParent } from './tree';
import type { FsFolder, FsNode } from './types';

/**
 * Path handling. Accepts forward or back slashes, is case-insensitive, and
 * collapses `.` / `..` so the address bar can be typed into freely.
 */
export function normalize(input: string): string {
  if (!input) return '';
  let p = input.trim().replace(/\//g, '\\');

  // `c:` alone means the drive root; `c:\` too.
  p = p.replace(/\\+/g, '\\').replace(/\\+$/, '');
  if (p === '') return '';

  const parts = p.split('\\');
  const out: string[] = [];
  for (const part of parts) {
    if (part === '.' || part === '') continue;
    if (part === '..') {
      out.pop();
      continue;
    }
    out.push(part);
  }
  return out.join('\\');
}

/** Canonical path with the tree's own capitalisation, when it resolves. */
export function canonical(path: string): string {
  return resolve(path)?.path ?? normalize(path);
}

/**
 * True when this path, or a folder somewhere above it, is in the Recycle Bin.
 *
 * It walks up rather than asking about the node itself because a deleted
 * folder takes its contents with it: nothing inside `My Music` is at its old
 * address once `My Music` has gone, and neither the tree nor the bin's mask
 * says so on its own.
 */
function isBinned(path: string): boolean {
  let cursor = path;
  while (cursor) {
    if (isDeletedPath(cursor)) return true;
    const cut = cursor.lastIndexOf('\\');
    if (cut === -1) return false;
    cursor = cursor.slice(0, cut);
  }
  return false;
}

/**
 * Look up a path. Deleted things are not at their old address any more — the
 * bytes are in the bin under a name of its choosing — so this stops finding
 * them the moment they go in, and finds them again when they come back.
 *
 * That is what makes a delete real everywhere at once: `TYPE`, the address
 * bar, Search and Run all ask this one question, and all get the same answer.
 * Restoring is the bin's own business, and works from the node it kept rather
 * than from the path, which is why it still works when nothing resolves.
 */
export function resolve(path: string): FsNode | undefined {
  const key = normalize(path).toLowerCase();
  if (!key) return undefined;
  const node = lookup(key);
  if (!node || isBinned(node.path)) return undefined;
  return node;
}

/**
 * Visible children of a folder; `[]` for files or unknown paths.
 *
 * The tree itself is the disk, and deleting does not cut nodes out of it —
 * the bin holds a mask, which is why it can put anything back. `resolve()`
 * above has already refused a folder that is in the bin, so this only has to
 * skip the children that are.
 */
export function list(path: string): FsNode[] {
  const node = resolve(path);
  if (!node || node.type !== 'folder') return [];
  return node.children.filter((child) => !child.hidden && !isDeletedPath(child.path));
}

export function parent(path: string): FsFolder | undefined {
  const node = resolve(path);
  if (!node) return undefined;
  return lookupParent(node.path.toLowerCase());
}

export function join(...parts: string[]): string {
  return normalize(parts.filter(Boolean).join('\\'));
}

export function basename(path: string): string {
  const n = normalize(path);
  const i = n.lastIndexOf('\\');
  return i === -1 ? n : n.slice(i + 1);
}

export function dirname(path: string): string {
  const n = normalize(path);
  const i = n.lastIndexOf('\\');
  return i === -1 ? '' : n.slice(0, i);
}

/** Breadcrumb trail from the root down to `path`, for Explorer's address bar. */
export function ancestors(path: string): FsNode[] {
  const chain: FsNode[] = [];
  let current = resolve(path);
  while (current) {
    chain.unshift(current);
    current = lookupParent(current.path.toLowerCase());
  }
  return chain;
}

/** Case-insensitive substring search over names, for Explorer's Search bar. */
export function search(term: string, within?: string): FsNode[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return [];
  const scopePrefix = within ? normalize(within).toLowerCase() : '';
  const results: FsNode[] = [];
  const walk = (node: FsNode) => {
    // A folder in the bin is not searched: what is inside it went in with it.
    if (isDeletedPath(node.path)) return;
    if (node.type === 'folder') for (const c of node.children) walk(c);
    if (node.hidden) return;
    if (scopePrefix && !node.path.toLowerCase().startsWith(scopePrefix)) return;
    if (node.name.toLowerCase().includes(needle)) results.push(node);
  };
  const start = within ? resolve(within) : lookup('my computer');
  if (start) walk(start);
  return results;
}
